import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { businessTypeToString } from '../../../utils/stringUtils';
import useSelectedBusiness from '../../../hooks/business/useSelectedBusiness';
import { createEmptyBusinessInvite } from '../../../utils/formatUtils';
import {
  BusinessInvite,
  CustomFieldType,
  CustomMultipleChoiceField,
} from '../../../services/model/inviteService.model';
import InviteForm from '../../components/forms/InviteForm';
import inviteService from '../../../services/inviteService';
import businessService from '../../../services/businessService';
import { filterCustomLabelFields } from '../../../utils/filterUtils';
import connectionService from '../../../services/connectionService';
import useBusinessTranslation from '../../../hooks/useBusinessTranslation';
import Button from '../../components/buttons/Button';
import useBusinessEffect from '../../../hooks/effects/useBusinessEffect';
import { isCommunity } from '../../../constants';
import WarningModal from '../../components/modals/WarningModal';
import DisableMemberAdministrationModal from '../../components/modals/DisableMemberAdministrationModal';

export default function RequestedDataPage(): JSX.Element {
  const [invite, setInvite] = useState<BusinessInvite>(createEmptyBusinessInvite());
  const [action, setAction] = useState<'CREATE' | 'UPDATE'>('CREATE');
  const [showNoDataModal, setShowNoDataModal] = useState(false);
  const [showDisableModal, setShowDisableModal] = useState(false);
  const { customFields } = invite;

  const business = useSelectedBusiness();
  const navigate = useNavigate();
  const { t } = useBusinessTranslation();

  useBusinessEffect(() => {
    const fetchInvite = async () => {
      const fields = await inviteService.fetchInvite();
      if (!fields) return;
      setInvite(fields);
      setAction('UPDATE');
    };
    fetchInvite();
    connectionService.getConnections();
  }, []);

  const handleCancel = async (): Promise<void> => {
    setInvite(business?.inviteFields || createEmptyBusinessInvite());
    navigate('/contacts', { state: { tab: 'Info' } });
  };

  const handleSubmit = async (): Promise<void> => {
    const hasSelectedFields = 
      invite.mandatoryFields.length > 0 || 
      invite.optionalFields.length > 0 || 
      invite.customFields.length > 0;

    if (!hasSelectedFields) {
      setShowNoDataModal(true);
      return;
    }

    const labelFields = filterCustomLabelFields(customFields);
    if (
      labelFields.some((field): boolean => !field.label) ||
      customFields
        .filter((x) => x.type === CustomFieldType.MULTIPLE_CHOICE)
        .map((x) => (x as CustomMultipleChoiceField).options)
        .flat()
        .some((option): boolean => !option)
    ) {
      toast.error(t('toast.error.allFields'));
      return;
    }
    if (
      Array.from(new Set(labelFields.map((field) => field.label))).length !== labelFields.length
    ) {
      toast.error(t('toast.error.noTwoSame'));
      return;
    }

    if (action === 'CREATE') await inviteService.createInvite(invite);
    if (action === 'UPDATE')
      await inviteService.updateInvite(invite).then(() => connectionService.getConnections(true));

    if (invite.nonRequestedFields.length !== 10) {
      //set the member adminstration feature to true if it is not already
      const features = { ...(business?.features || {}) };
      if (!features.MEMBER_ADMINISTRATION) {
        features.MEMBER_ADMINISTRATION = true;
        await businessService.updateBusiness(
          business?.name || '',
          business?.description || '',
          features,
          business?.memberPreferences || {},
        );
      }
    }

    await businessService.fetchBusinesses();

    toast.success(
      action === 'UPDATE' ? t('toast.success.inviteUpdated') : t('toast.success.inviteCreated'),
    );
    if (isCommunity && action === 'CREATE') navigate('/invite-link', { state: { view: 'SETUP' } });
    else navigate('/contacts', { state: { tab: 'Info' } });
  };

  const handleDisableMemberAdministration = async (): Promise<void> => {
    try {
      // Clear all invite data
      const clearedInvite = {
        ...invite,
        mandatoryFields: [],
        optionalFields: [],
        nonRequestedFields: [...invite.nonRequestedFields, ...invite.mandatoryFields, ...invite.optionalFields],
        customFields: [],
      };
      
      await inviteService.updateInvite(clearedInvite);
      
      // Disable the member administration feature
      const features = { ...(business?.features || {}) };
      features.MEMBER_ADMINISTRATION = false;
      
      await businessService.updateBusiness(
        business?.name || '',
        business?.description || '',
        features,
        business?.memberPreferences || {},
      );
      
      await businessService.fetchBusinesses();
      
      toast.success(t('toast.success.businessSaved'));
      navigate('/contacts');
    } catch (error) {
      console.error('Failed to disable member administration:', error);
      toast.error(t('toast.error.unexpected'));
    }
  };

  return (
    <>
      {/* Header */}
      <div className="mb-6 flex flex-col border-b border-primary-300 pb-6">
        <h1 className="mb-2 text-3xl font-semibold">
          {t('page.settings.requested.title')}
        </h1>
        <p className="lg:w-1/2">
          {business?.inviteId
            ? t('page.settings.requested.subtitle')
            : t('page.settings.subtitle', { type: businessTypeToString(business?.type!) })}
        </p>
      </div>

      <div className="flex flex-col gap-6 lg:flex-row">
        <div className="w-full pb-4">
          <InviteForm invite={invite} setInvite={setInvite} />
        </div>
        <div className="mt-auto flex w-full flex-col justify-between">
          <div className="mb-4 flex w-full border-b border-secondary-200 pb-4 lg:mb-0 lg:mt-0 lg:justify-end lg:border-b-0 lg:pb-0">
            <Button
              onClick={handleCancel}
              className="px-0 font-medium underline disabled:opacity-20">
              {t('general.cancel')}
            </Button>
            <Button variant="primary" onClick={handleSubmit} className="ml-6 disabled:opacity-20">
              {t('general.save')}
            </Button>
          </div>
        </div>
      </div>
      
      <WarningModal
        open={showNoDataModal}
        setOpen={setShowNoDataModal}
        title={t('toast.error.noDataSelected')}
        description={t('page.settings.requested.noDataModal.description')}
        button={t('page.settings.requested.noDataModal.selectData')}
        cancel={t('page.settings.requested.noDataModal.disableFeature')}
        onCancel={() => {
          setShowNoDataModal(false);
          setShowDisableModal(true);
        }}
      />
      
      <DisableMemberAdministrationModal
        open={showDisableModal}
        setOpen={setShowDisableModal}
        currentInvite={invite}
        onConfirmDisable={handleDisableMemberAdministration}
      />
    </>
  );
}
